import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Text,
  Button,
  Plus,
  LoadingOrbit,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextField,
  Select,
  useToast,
  DocsAnnotation,
  ListCard,
  EmptyState,
  Box,
  Flex,
  PageHeader,
} from '@chatsdet/ui';
import { CloudArrowUp, Plus as PlusIcon, Warning } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError, ApiError } from '../../../lib/api-error';
import { isValidLoginPath, type EnvVarDefinition } from './property-fields';
import { SensitiveRevealField } from '../SensitiveRevealField';
import { useEnvironmentTemplates } from '../../../hooks/useEnvironmentTemplates';
import { fetchOrgPropertyById } from '../../../hooks/useOrgProperty';
import {
  isVisibleEnvironmentListItem,
  type EnvironmentItem,
} from './propertyEnvironmentPresentation';
import { PropertyEnvironmentRow } from './PropertyEnvironmentRow';
import { SandboxV2EnvironmentFields } from './SandboxV2EnvironmentFields';
import { EnvironmentStageSelect } from '../../../features/properties/components/EnvironmentStageSelect';

export { formatInstanceDescriptor } from './propertyEnvironmentPresentation';

/**
 * Prepare env-var values for PUT: drop empty values (= unset). Masked values
 * pass through untouched — the server substitutes the existing stored value
 * for the mask, so an unedited sensitive field survives a save.
 */
export function prepareEnvVarValuesForSave(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

async function errorFromResponse(r: Response): Promise<Error> {
  const text = await r.text();
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (parsed && typeof parsed.error === 'string') return new Error(parsed.error);
  } catch {
    // fall through
  }
  return new Error(text || `Request failed (${r.status})`);
}

type EnvironmentFormState = {
  name: string;
  environmentKindId: string | null;
  environmentKindName: string;
  url: string;
  type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
  sandboxTemplateId: string;
  expiresAt: string;
  description: string;
  loginPath: string;
  envVarValues: Record<string, string>;
  vpcEnabled: boolean;
  customerAwsRoleArn: string;
  customerSubnetId: string;
  customerSecurityGroupId: string;
  customerRegion: string;
};

const defaultFormState: EnvironmentFormState = {
  name: '',
  environmentKindId: null,
  environmentKindName: '',
  url: '',
  type: 'permanent',
  sandboxTemplateId: '',
  expiresAt: '',
  description: '',
  loginPath: '',
  envVarValues: {},
  vpcEnabled: false,
  customerAwsRoleArn: '',
  customerSubnetId: '',
  customerSecurityGroupId: '',
  customerRegion: 'us-east-1',
};

export function PropertyEnvironmentsTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<EnvironmentItem | null>(null);
  const [form, setForm] = React.useState<EnvironmentFormState>(defaultFormState);
  // A v2 environment is synced from the application's cofactor.sandbox.yaml;
  // it can't be created here, and its backing is shown read-only.
  const editingV2Environment = editing?.sandboxV2Environment ?? null;

  // Verified templates scoped to this property — the options for a sandbox
  // environment's v1 backing template.
  const templatesQ = useEnvironmentTemplates({ status: 'verified' });
  const propertyTemplates = React.useMemo(
    () =>
      (templatesQ.data?.data ?? []).filter(
        (t: { propertyId?: string | null }) => t.propertyId === propertyId
      ),
    [templatesQ.data?.data, propertyId]
  );
  const canUseV1Template = propertyTemplates.length > 0;
  const canUseSandboxType = canUseV1Template || !!editingV2Environment;

  const environmentsQ = useQuery({
    queryKey: ['property-environments', propertyId],
    queryFn: async (): Promise<{ items: EnvironmentItem[] }> => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/environments`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch environments');
      return r.json();
    },
  });

  // The property declares which env vars exist (and which are sensitive); each
  // environment provides values for them in the dialog below.
  const propertyQ = useQuery({
    queryKey: ['property', propertyId],
    queryFn: async () => {
      return fetchOrgPropertyById<{
        id: string;
        envVarDefinitions?: EnvVarDefinition[] | null;
        loginPath?: string | null;
      }>(propertyId);
    },
  });
  const envVarDefinitions = React.useMemo(
    () => (propertyQ.data?.envVarDefinitions ?? []).filter((d) => d.name.trim() !== ''),
    [propertyQ.data?.envVarDefinitions]
  );

  const allItems = environmentsQ.data?.items ?? [];
  // Hide ephemeral environments that expired more than 24h ago — they're sandbox debris.
  const items = allItems.filter((item) => isVisibleEnvironmentListItem(item));
  const defaultEnvironmentId = items.find((item) => item.isDefault)?.id ?? null;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const isSandbox = form.type === 'sandbox';
      const trimmedUrl = form.url.trim();
      const normalizedUrl = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
      const payload: Record<string, unknown> = {
        name: form.name,
        environmentKindId: form.environmentKindId,
        environmentKindName:
          !form.environmentKindId && form.environmentKindName.trim()
            ? form.environmentKindName.trim()
            : undefined,
        // Sandbox URLs are stamped from the backing instance by the server.
        url: isSandbox ? undefined : normalizedUrl,
        type: form.type,
        sandboxTemplateId: isSandbox && !editingV2Environment ? form.sandboxTemplateId : null,
        expiresAt: form.expiresAt || undefined,
        description: form.description || undefined,
        // Empty string clears the override (falls back to the property login path)
        loginPath: form.loginPath || null,
      };

      // Masked sensitive values pass through; the server swaps them for the
      // stored values so an unedited field survives the save.
      if (envVarDefinitions.length > 0) {
        payload.envVarValues = prepareEnvVarValuesForSave(form.envVarValues);
      }

      if (form.vpcEnabled) {
        payload.customerAwsRoleArn = form.customerAwsRoleArn || null;
        payload.customerSubnetId = form.customerSubnetId || null;
        payload.customerSecurityGroupId = form.customerSecurityGroupId || null;
        payload.customerRegion = form.customerRegion || 'us-east-1';
      } else {
        // Clear VPC fields when toggle is off
        payload.customerAwsRoleArn = null;
        payload.customerSubnetId = null;
        payload.customerSecurityGroupId = null;
        payload.customerRegion = null;
      }
      const url = editing
        ? `${API_URL}/org/properties/${propertyId}/environments/${editing.id}`
        : `${API_URL}/org/properties/${propertyId}/environments`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to save environment');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-environments', propertyId] });
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
      qc.invalidateQueries({ queryKey: ['environment-kinds'] });
      toast.success(editing ? 'Environment updated' : 'Environment created');
      setDialogOpen(false);
      setEditing(null);
      setForm(defaultFormState);
    },
    onError: (error) => {
      // Typed user errors also render inline in the dialog below — the toast is a secondary signal.
      toast.error(error instanceof Error ? error.message : 'Failed to save environment');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (environmentId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/environments/${environmentId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!r.ok) throw await errorFromResponse(r);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-environments', propertyId] });
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
      toast.success('Environment deleted');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete environment');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (environmentId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/environments/${environmentId}/set-default`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!r.ok) throw await errorFromResponse(r);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-environments', propertyId] });
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
      toast.success('Default environment updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to set default environment');
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(defaultFormState);
    setDialogOpen(true);
  };

  const openEdit = (environment: EnvironmentItem) => {
    setEditing(environment);
    const hasVpc = !!(
      environment.customerAwsRoleArn &&
      environment.customerSubnetId &&
      environment.customerSecurityGroupId
    );
    setForm({
      name: environment.name,
      environmentKindId: environment.environmentKindId,
      environmentKindName: environment.environmentKindName ?? '',
      url: environment.url,
      type: environment.type,
      sandboxTemplateId: environment.sandboxTemplateId ?? '',
      expiresAt: environment.expiresAt ? new Date(environment.expiresAt).toISOString() : '',
      description: environment.description ?? '',
      loginPath: environment.loginPath ?? '',
      envVarValues: environment.envVarValues ?? {},
      vpcEnabled: hasVpc,
      customerAwsRoleArn: environment.customerAwsRoleArn ?? '',
      customerSubnetId: environment.customerSubnetId ?? '',
      customerSecurityGroupId: environment.customerSecurityGroupId ?? '',
      customerRegion: environment.customerRegion ?? 'us-east-1',
    });
    setDialogOpen(true);
  };

  const loginPathInvalid = !isValidLoginPath(form.loginPath);

  const isEmpty = !environmentsQ.isLoading && items.length === 0;

  return (
    <DocsAnnotation slug="reference/properties" title="Application Environments">
      <Box className="space-y-4">
        <PageHeader
          size="section"
          title="Environments"
          description="Manage target URLs for this application."
          actions={
            !isEmpty ? (
              <Button
                variant="primary"
                size="sm"
                onPress={openCreate}
                iconLeft={<Plus size={16} />}
              >
                Add Environment
              </Button>
            ) : undefined
          }
        />
        <ListCard>
          {environmentsQ.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingOrbit size="md" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<CloudArrowUp size={24} />}
              title="No environments yet"
              description="Environments are the target URLs your tests run against — staging, production, sandbox, or anywhere else you deploy. Add one to get started."
              action={
                <Button variant="primary" onPress={openCreate}>
                  <PlusIcon size={16} />
                  Add Environment
                </Button>
              }
            />
          ) : (
            items.map((env, index) => (
              <PropertyEnvironmentRow
                key={env.id}
                propertyId={propertyId}
                environment={env}
                index={index}
                isOnlyEnvironment={items.length <= 1}
                onEdit={openEdit}
                onDelete={(environmentId) => deleteMutation.mutate(environmentId)}
                onSetDefault={(environmentId) => setDefaultMutation.mutate(environmentId)}
              />
            ))
          )}

          <DialogTrigger isOpen={dialogOpen} onOpenChange={setDialogOpen}>
            <span />
            <Dialog title={editing ? 'Edit Environment' : 'Add Environment'}>
              <DialogContent>
                <div className="space-y-3">
                  {saveMutation.isError && (
                    <Flex
                      align="center"
                      gap="sm"
                      className="p-3 rounded-md bg-destructive/10 text-destructive"
                    >
                      <Warning size={20} />
                      <Text size="sm">
                        {saveMutation.error instanceof ApiError
                          ? saveMutation.error.message
                          : 'Failed to save environment'}
                      </Text>
                    </Flex>
                  )}
                  <TextField
                    label="Name"
                    value={form.name}
                    onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
                    isRequired
                  />
                  {form.type !== 'ephemeral' && (
                    <EnvironmentStageSelect
                      stageId={form.environmentKindId}
                      stageName={form.environmentKindName}
                      allowUnassigned
                      onChange={(stage) =>
                        setForm((prev) => ({
                          ...prev,
                          environmentKindId: stage.id,
                          environmentKindName: stage.name,
                        }))
                      }
                    />
                  )}
                  {form.type !== 'sandbox' && (
                    <TextField
                      label="URL"
                      value={form.url}
                      onChange={(value) => setForm((prev) => ({ ...prev, url: value }))}
                      isRequired
                    />
                  )}
                  {!editingV2Environment && (
                  <Select
                    label="Type"
                    items={[
                      { id: 'permanent', label: 'Permanent' },
                      { id: 'ephemeral', label: 'Ephemeral' },
                      ...(canUseSandboxType ? [{ id: 'sandbox', label: 'Sandbox' }] : []),
                    ]}
                    selectedKey={form.type}
                    onSelectionChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        type:
                          value === 'ephemeral'
                            ? 'ephemeral'
                            : value === 'sandbox'
                              ? 'sandbox'
                              : 'permanent',
                        ...(value === 'ephemeral'
                          ? { environmentKindId: null, environmentKindName: '' }
                          : {}),
                      }))
                    }
                  />
                  )}
                  {form.type === 'sandbox' && (
                    <div className="space-y-3">
                      {editingV2Environment ? (
                        <SandboxV2EnvironmentFields
                          applicationId={propertyId}
                          environment={editingV2Environment}
                        />
                      ) : (
                        <div className="space-y-1">
                          <Select
                            label="Sandbox template"
                            items={propertyTemplates.map((t: { id: string; name: string }) => ({
                              id: t.id,
                              label: t.name,
                            }))}
                            placeholder="Select a verified template..."
                            selectedKey={form.sandboxTemplateId || null}
                            onSelectionChange={(value) =>
                              setForm((prev) => ({ ...prev, sandboxTemplateId: value as string }))
                            }
                          />
                          <Text size="xs" className="text-muted-foreground">
                            The environment's URL comes from the live sandbox instance. The
                            instance starts automatically when you author or run against this
                            environment and sleeps ~30 minutes after the last activity.
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                  {form.type === 'ephemeral' && (
                    <TextField
                      label="Expires At (ISO datetime)"
                      value={form.expiresAt}
                      onChange={(value) => setForm((prev) => ({ ...prev, expiresAt: value }))}
                      placeholder="2026-02-10T12:00:00.000Z"
                    />
                  )}
                  <TextField
                    label="Description"
                    value={form.description}
                    onChange={(value) => setForm((prev) => ({ ...prev, description: value }))}
                  />
                  <div>
                    <TextField
                      label="Login Path Override"
                      value={form.loginPath}
                      onChange={(value) => setForm((prev) => ({ ...prev, loginPath: value }))}
                      placeholder={propertyQ.data?.loginPath || '/login'}
                      description="Relative path appended to this environment's URL when starting a login. Leave empty to use the application's login path."
                    />
                    {loginPathInvalid && (
                      <Text size="xs" className="text-destructive mt-1">
                        Login path must be a relative path starting with "/" (e.g. /login), not a
                        full URL.
                      </Text>
                    )}
                  </div>

                  {envVarDefinitions.length > 0 && (
                    <div className="border-t pt-3 mt-3 space-y-3">
                      <div>
                        <Text size="sm" weight="medium">
                          Environment Variables
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          Values for this environment, injected at run time as{' '}
                          <code className="text-xs">$var.ENV.&lt;NAME&gt;</code>. Sensitive values
                          display masked; leave them untouched to keep the stored value.
                        </Text>
                      </div>
                      {envVarDefinitions.map((def) => {
                        const value = form.envVarValues[def.name] ?? '';
                        const onChange = (nextValue: string) =>
                          setForm((prev) => ({
                            ...prev,
                            envVarValues: { ...prev.envVarValues, [def.name]: nextValue },
                          }));

                        const environmentId = editing?.id ?? null;
                        return def.sensitive ? (
                          <SensitiveRevealField
                            key={def.name}
                            revealUrl={
                              environmentId
                                ? `${API_URL}/org/properties/${encodeURIComponent(propertyId)}/environments/${encodeURIComponent(environmentId)}/env-vars/${encodeURIComponent(def.name)}/reveal`
                                : null
                            }
                            label={def.name}
                            value={value}
                            onChange={onChange}
                            // A sensitive definition's description may have been
                            // authored before the value/mask split existed. Never
                            // render it beside a secret field, where it could
                            // accidentally disclose the value itself.
                            description="Encrypted value. Use the eye icon to reveal it when needed."
                          />
                        ) : (
                          <TextField
                            key={def.name}
                            label={def.name}
                            value={value}
                            onChange={onChange}
                            description={def.description}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* VPC config UI hidden for now — form state + submit logic preserved so
                  existing VPC-configured environments keep their settings on edit. */}

                  {defaultEnvironmentId && items.length === 1 && (
                    <Text size="xs" className="text-muted-foreground">
                      A property must always keep at least one environment.
                    </Text>
                  )}
                </div>
              </DialogContent>
              <DialogFooter>
                <Button
                  variant="outline"
                  onPress={() => setDialogOpen(false)}
                  isDisabled={saveMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onPress={() => saveMutation.mutate()}
                  isDisabled={
                    saveMutation.isPending ||
                    !form.name ||
                    (form.type === 'sandbox'
                      ? !editingV2Environment && !form.sandboxTemplateId
                      : !form.url) ||
                    loginPathInvalid ||
                    (form.vpcEnabled &&
                      (!form.customerAwsRoleArn ||
                        !form.customerSubnetId ||
                        !form.customerSecurityGroupId))
                  }
                >
                  {saveMutation.isPending ? 'Saving...' : editing ? 'Save' : 'Create'}
                </Button>
              </DialogFooter>
            </Dialog>
          </DialogTrigger>
        </ListCard>
      </Box>
    </DocsAnnotation>
  );
}
